import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faPlus,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  ApiClientError,
  getLibraryLessons,
  postCourseAiGenerate,
  postCourseAiRefine,
  postTeacherCourse,
  type CourseAiDraft,
  type TeacherLessonRecord,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { AutoResizeTextarea } from "../components/AutoResizeTextarea";
import LessonAiProgressPanel from "./LessonAiProgressPanel";
import { LessonIcon } from "./LessonIcon";
import { AI_SPEND_TOOLTIP, AiSpendIcon } from "./ai-spend";
import { MentionInput } from "./standards-mentions/MentionInput";
import {
  emptyMentionDoc,
  isMentionDocEmpty,
  toAiPromptText,
  toLessonAiStandardsRequest,
  type MentionPromptDoc,
} from "./standards-mentions/mentionTypes";
import { useDefaultStandardSetId } from "./standards-mentions/useDefaultStandardSetId";
import { useStandardsCatalog } from "./standards-mentions/useStandardsCatalog";
import {
  startLessonAiProgressClock,
  type LessonAiProgressUpdate,
} from "./lesson-ai-progress";
import { reorderLessonList } from "./TemplateLessonSequenceRail";
import { sortLessonLibrary } from "./lessonLibrarySort";

const EMPTY_DRAFT: CourseAiDraft = {
  title: "",
  description: "",
  lessonIds: [],
};

function draftsEqual(a: CourseAiDraft, b: CourseAiDraft): boolean {
  return (
    a.title === b.title &&
    a.description === b.description &&
    (a.rationale ?? "") === (b.rationale ?? "") &&
    a.lessonIds.length === b.lessonIds.length &&
    a.lessonIds.every((id, i) => id === b.lessonIds[i])
  );
}

export default function TeacherCourseAiStudioPage() {
  const navigate = useNavigate();
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();

  const [libraryLessons, setLibraryLessons] = useState<TeacherLessonRecord[]>([]);
  const [draft, setDraft] = useState<CourseAiDraft>(EMPTY_DRAFT);
  const [savedBaseline, setSavedBaseline] = useState<CourseAiDraft>(EMPTY_DRAFT);
  const [mentionDoc, setMentionDoc] = useState<MentionPromptDoc>(emptyMentionDoc);
  const { setId: defaultSetId } = useDefaultStandardSetId(bearer);
  const [standardSetId, setStandardSetId] = useState("");
  const catalog = useStandardsCatalog(bearer, standardSetId || defaultSetId);
  const activeSetId = standardSetId || defaultSetId;

  useEffect(() => {
    if (defaultSetId && !standardSetId) setStandardSetId(defaultSetId);
  }, [defaultSetId, standardSetId]);

  const [loadingLibrary, setLoadingLibrary] = useState(true);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiProgressMode, setAiProgressMode] = useState<"template-generate" | "template-refine" | null>(null);
  const [aiProgress, setAiProgress] = useState<LessonAiProgressUpdate | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [upgradeCta, setUpgradeCta] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [addPickId, setAddPickId] = useState("");

  const lessonById = useMemo(() => new Map(libraryLessons.map((l) => [l.id, l])), [libraryLessons]);

  const orderedLessons = useMemo(
    () =>
      draft.lessonIds
        .map((id) => lessonById.get(id))
        .filter((l): l is TeacherLessonRecord => Boolean(l)),
    [draft.lessonIds, lessonById]
  );

  const addOptions = useMemo(
    () => sortLessonLibrary(libraryLessons.filter((l) => !draft.lessonIds.includes(l.id)), "title"),
    [libraryLessons, draft.lessonIds]
  );

  const hasUnsavedChanges = useMemo(() => !draftsEqual(draft, savedBaseline), [draft, savedBaseline]);

  const refreshLibrary = useCallback(async () => {
    if (!bearer) return;
    setLoadingLibrary(true);
    setError(null);
    try {
      const data = await getLibraryLessons(bearer, { limit: 200 });
      setLibraryLessons(data.lessons);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load your lesson library");
      setLibraryLessons([]);
    } finally {
      setLoadingLibrary(false);
    }
  }, [bearer]);

  useEffect(() => {
    void refreshLibrary();
  }, [refreshLibrary]);

  useEffect(() => {
    setAddPickId(addOptions[0]?.id ?? "");
  }, [addOptions]);

  useEffect(() => {
    const onBeforeUnload = (ev: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) return;
      ev.preventDefault();
      ev.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsavedChanges]);

  async function runWithAiProgress<T>(
    mode: "template-generate" | "template-refine",
    work: () => Promise<T>
  ): Promise<T | undefined> {
    setAiBusy(true);
    setAiProgressMode(mode);
    setAiProgress(null);
    const stopClock = startLessonAiProgressClock(mode, setAiProgress);
    try {
      return await work();
    } finally {
      stopClock();
      setAiBusy(false);
      setAiProgressMode(null);
      setAiProgress(null);
    }
  }

  async function handleGenerate() {
    if (!bearer || isMentionDocEmpty(mentionDoc)) return;
    if (libraryLessons.length === 0) {
      setError("Add at least one lesson to My lessons before generating a course.");
      return;
    }
    const prompt = toAiPromptText(mentionDoc);
    const standardsReq = toLessonAiStandardsRequest(mentionDoc, activeSetId || undefined);
    setError(null);
    setUpgradeCta(null);
    try {
      const seed =
        draft.title.trim() || draft.description.trim() || draft.lessonIds.length > 0 ? draft : undefined;
      const result = await runWithAiProgress("template-generate", () =>
        postCourseAiGenerate(bearer, {
          prompt,
          ...(seed ? { draft: seed } : {}),
          ...(standardsReq ?? {}),
        })
      );
      if (!result) return;
      setDraft(result.draft);
      setMentionDoc(emptyMentionDoc());
      setSaveSuccess(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Generation failed";
      setError(msg);
      if (e instanceof ApiClientError) {
        if (e.code === "BILLING_REQUIRED") setUpgradeCta("Manage billing to continue using AI tools.");
        else if (e.code === "AI_LIMIT_REACHED") setUpgradeCta("See your AI usage on the billing page.");
      }
    }
  }

  async function handleRefine() {
    if (!bearer || isMentionDocEmpty(mentionDoc)) return;
    if (!draft.title.trim() || draft.lessonIds.length === 0) {
      setError("Generate a draft with at least one lesson before refining.");
      return;
    }
    const prompt = toAiPromptText(mentionDoc);
    const standardsReq = toLessonAiStandardsRequest(mentionDoc, activeSetId || undefined);
    setError(null);
    setUpgradeCta(null);
    try {
      const result = await runWithAiProgress("template-refine", () =>
        postCourseAiRefine(bearer, {
          prompt,
          draft,
          ...(standardsReq ?? {}),
        })
      );
      if (!result) return;
      setDraft(result.draft);
      setMentionDoc(emptyMentionDoc());
      setSaveSuccess(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Refinement failed";
      setError(msg);
      if (e instanceof ApiClientError) {
        if (e.code === "BILLING_REQUIRED") setUpgradeCta("Manage billing to continue using AI tools.");
        else if (e.code === "AI_LIMIT_REACHED") setUpgradeCta("See your AI usage on the billing page.");
      }
    }
  }

  function moveLesson(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= draft.lessonIds.length) return;
    setDraft((d) => ({
      ...d,
      lessonIds: reorderLessonList(d.lessonIds, index, direction < 0 ? index - 1 : index + 2),
    }));
  }

  function removeLesson(lessonId: string) {
    setDraft((d) => ({ ...d, lessonIds: d.lessonIds.filter((id) => id !== lessonId) }));
  }

  function addLesson(lessonId: string) {
    if (!lessonId || draft.lessonIds.includes(lessonId)) return;
    setDraft((d) => ({ ...d, lessonIds: [...d.lessonIds, lessonId] }));
  }

  async function handleSave() {
    if (!bearer || saving) return;
    if (!draft.title.trim()) {
      setError("Add a template title before saving.");
      return;
    }
    if (draft.lessonIds.length === 0) {
      setError("Pick at least one lesson for the sequence.");
      return;
    }
    setSaving(true);
    setError(null);
    setUpgradeCta(null);
    setSaveSuccess(null);
    try {
      const { course } = await postTeacherCourse(bearer, {
        title: draft.title.trim(),
        description: draft.description.trim() || undefined,
        lessonIds: draft.lessonIds,
      });
      setSavedBaseline(draft);
      setSaveSuccess(`Saved “${course.title}”. You can fine-tune the sequence on the next screen.`);
      navigate(`/teacher/courses/${course.id}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Save failed";
      setError(msg);
      if (e instanceof ApiClientError && (e.code === "BILLING_REQUIRED" || e.code === "LESSON_LIMIT_REACHED")) {
        setUpgradeCta("Upgrade your plan in Billing to continue creating templates.");
      }
    } finally {
      setSaving(false);
    }
  }

  const promptPlaceholder =
    libraryLessons.length === 0
      ? "Create lessons in My lessons first, then describe the course you want to assemble…"
      : "e.g. Build a gentle intro-to-Python path for 6th graders using my loops and variables lessons, ending with a mini game project.";

  if (loadingLibrary) {
    return (
      <div className="teacher-page">
        <p className="muted">Loading your lesson library…</p>
      </div>
    );
  }

  return (
    <div className="teacher-page teacher-ai-studio teacher-ai-studio--template">
      <header className="teacher-page-header">
        <h1 className="teacher-page-title">Create course with AI</h1>
        <p className="teacher-page-lede muted">
          Describe the course you want. AI picks and orders lessons from your library, then you tweak the path and
          save the template.
        </p>
        <div className="teacher-toolbar-form">
          <Link to="/teacher/courses" className="btn secondary" style={{ textDecoration: "none" }}>
            Back to templates
          </Link>
          <Link to="/teacher/lessons" className="btn secondary" style={{ textDecoration: "none" }}>
            My lessons
          </Link>
        </div>
      </header>

      {libraryLessons.length === 0 ? (
        <div className="teacher-callout-muted" role="note">
          <p>
            You need lessons in your library first.{" "}
            <Link to="/teacher/lessons/new">Create a lesson</Link>, then come back here.
          </p>
        </div>
      ) : null}

      {saveSuccess ? (
        <div className="teacher-callout-muted" role="status">
          <p>{saveSuccess}</p>
        </div>
      ) : null}

      {error ? (
        <div className="warn-banner" role="alert">
          {error}
          {upgradeCta ? (
            <>
              {" "}
              <Link to="/teacher/billing">Manage billing</Link>
            </>
          ) : null}
        </div>
      ) : null}

      {aiBusy && aiProgress && aiProgressMode ? (
        <LessonAiProgressPanel mode={aiProgressMode} progress={aiProgress} />
      ) : null}

      <div className={`teacher-ai-studio-layout${aiBusy ? " teacher-ai-studio-layout--busy" : ""}`}>
        <div className="teacher-ai-studio-author">
          <section className="teacher-panel">
            <h2 className="teacher-panel-title">Prompt</h2>
            <MentionInput
              bearer={bearer}
              value={mentionDoc}
              onChange={(doc) => {
                setMentionDoc(doc);
                setSaveSuccess(null);
              }}
              standardSetId={activeSetId}
              onStandardSetIdChange={setStandardSetId}
              standardSets={catalog.sets}
              showSetSelector={catalog.sets.length > 1}
              disabled={aiBusy || libraryLessons.length === 0}
              minRows={4}
              placeholder={`${promptPlaceholder} Type @ to tag a standard or glossary term.`}
            />
            <div className="teacher-toolbar-form">
              <button
                type="button"
                className="btn primary"
                disabled={aiBusy || isMentionDocEmpty(mentionDoc) || libraryLessons.length === 0}
                title={AI_SPEND_TOOLTIP}
                onClick={() => void handleGenerate()}
              >
                <AiSpendIcon /> {aiBusy ? "Working…" : draft.title.trim() ? "Regenerate from prompt" : "Generate course path"}
              </button>
              <button
                type="button"
                className="btn secondary"
                disabled={
                  aiBusy ||
                  isMentionDocEmpty(mentionDoc) ||
                  !draft.title.trim() ||
                  draft.lessonIds.length === 0
                }
                title={AI_SPEND_TOOLTIP}
                onClick={() => void handleRefine()}
              >
                <AiSpendIcon /> Refine with prompt
              </button>
            </div>
            <p className="muted small" style={{ marginTop: "0.5rem", marginBottom: 0 }}>
              AI only uses lessons you already own. It won&apos;t invent new ones. You can add, remove, or reorder
              before saving.
            </p>
          </section>

          <section className="teacher-panel">
            <h2 className="teacher-panel-title">Template fields</h2>
            <label className="field">
              <span className="field-label">Title</span>
              <input
                className="input"
                value={draft.title}
                onChange={(ev) => setDraft((d) => ({ ...d, title: ev.target.value }))}
                placeholder="e.g. Intro to Python, Fall 2026"
              />
            </label>
            <label className="field">
              <span className="field-label">Description</span>
              <AutoResizeTextarea
                className="input"
                minRows={3}
                value={draft.description}
                onChange={(ev) => setDraft((d) => ({ ...d, description: ev.target.value }))}
                placeholder="Who this course is for and what students accomplish across the sequence."
              />
            </label>
            {draft.rationale ? (
              <div className="teacher-callout-muted">
                <p className="small">
                  <strong>AI rationale:</strong> {draft.rationale}
                </p>
              </div>
            ) : null}
            <button
              type="button"
              className="btn primary"
              disabled={saving || !draft.title.trim() || draft.lessonIds.length === 0}
              onClick={() => void handleSave()}
            >
              {saving ? "Saving template…" : "Save course"}
            </button>
          </section>
        </div>

        <section className="teacher-panel teacher-template-ai-preview">
          <div className="teacher-template-ai-preview-header">
            <div>
              <h2 className="teacher-panel-title">Lesson sequence preview</h2>
              <p className="muted small">
                {orderedLessons.length === 0
                  ? "Generate a path to see lessons here."
                  : `${orderedLessons.length} lesson${orderedLessons.length === 1 ? "" : "s"} selected`}
              </p>
            </div>
            {addOptions.length > 0 ? (
              <div className="teacher-template-ai-add-row">
                <select
                  className="input"
                  value={addPickId}
                  onChange={(ev) => setAddPickId(ev.target.value)}
                  aria-label="Add a lesson from library"
                >
                  {addOptions.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.title}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn secondary tight"
                  disabled={!addPickId}
                  onClick={() => addLesson(addPickId)}
                  title="Add lesson"
                >
                  <FontAwesomeIcon icon={faPlus} fixedWidth />
                </button>
              </div>
            ) : null}
          </div>

          {orderedLessons.length === 0 ? (
            <p className="muted teacher-sequence-empty">Your library has {libraryLessons.length} lessons ready to assemble.</p>
          ) : (
            <ol className="teacher-template-ai-sequence">
              {orderedLessons.map((l, index) => (
                <li key={l.id} className="teacher-sequence-card teacher-template-ai-sequence-card">
                  <span className="teacher-sequence-card-step" aria-hidden>
                    {index + 1}
                  </span>
                  <LessonIcon
                    lessonId={l.id}
                    title={l.title}
                    iconName={l.iconName}
                    bgColor={l.iconBgColor}
                    fgColor={l.iconFgColor}
                    workspaceMode={l.workspaceMode}
                    size={3.25}
                    className="teacher-sequence-card-icon"
                  />
                  <div className="teacher-sequence-card-body">
                    <span className="teacher-sequence-card-title">{l.title}</span>
                    {l.description ? (
                      <span className="teacher-sequence-card-desc">{l.description}</span>
                    ) : (
                      <span className="teacher-sequence-card-desc is-placeholder">No description</span>
                    )}
                  </div>
                  <div className="teacher-template-ai-sequence-actions">
                    <button
                      type="button"
                      className="teacher-sequence-card-remove"
                      disabled={index === 0}
                      onClick={() => moveLesson(index, -1)}
                      title="Move up"
                      aria-label={`Move ${l.title} up`}
                    >
                      <FontAwesomeIcon icon={faArrowUp} fixedWidth />
                    </button>
                    <button
                      type="button"
                      className="teacher-sequence-card-remove"
                      disabled={index === orderedLessons.length - 1}
                      onClick={() => moveLesson(index, 1)}
                      title="Move down"
                      aria-label={`Move ${l.title} down`}
                    >
                      <FontAwesomeIcon icon={faArrowDown} fixedWidth />
                    </button>
                    <button
                      type="button"
                      className="teacher-sequence-card-remove"
                      onClick={() => removeLesson(l.id)}
                      title="Remove from sequence"
                      aria-label={`Remove ${l.title}`}
                    >
                      <FontAwesomeIcon icon={faXmark} fixedWidth />
                    </button>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
}
